import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import translations from './practicetypes.i18n';
import classNames from 'classnames';
import * as commonCss from './avtalesectionstyles.module.scss';
import * as css from './practicetypes.module.scss';
import Button from '../../components/button';
import {
  Table,
  TableCellTd,
  TableCellTh,
  TableHeader,
  TableMain,
  TableRow,
} from '../../components/table/alpTable';
import {
  EmneDto,
  PraksistypeDto,
  PraksistypeMedEmnerDto,
} from '../../api/generated/studiested-generated-api';
import { StudiestedApiContext } from '../../api/StudiestedApiContext';
import { useLoader } from '../../api/useLoader';
import { ConfirmationDialog } from '../../components/dialog';
import Autocomplete from '../../components/input/autocomplete';
import Spinner from '../../components/spinner/spinner';
import useDynamicDocumentAndElementHeights from '../../hooks/useDynamicDocumentAndElementHeights';
import {
  AgreementStateActionType,
  getCssBasedOnModalHeight,
  useAgreementContext,
} from './index';

/*
 * EmneDto and PraksistypeDto are currently interchangeable
 * This means that a: EmneDto and b: PraksistypeDto may be deemed equal
 * Keep this in mind, when using method
 * */
const praksistyperOrEmnerAreEqual = (
  a: PraksistypeDto | EmneDto,
  b: PraksistypeDto | EmneDto,
) => a.kode === b.kode && a.navn === b.navn;

const usePraksistyperHasChanged = (): ((
  praksistyper: PraksistypeDto[],
) => boolean) => {
  const { initAgreement } = useAgreementContext();

  return (praksistyper: PraksistypeDto[]) => {
    if (!initAgreement?.praksistyper) return true;
    if (initAgreement.praksistyper.length === 0 && praksistyper.length === 0) {
      return false;
    }

    return (
      initAgreement.praksistyper.length !== praksistyper.length ||
      !initAgreement.praksistyper.every((initPraksistype) =>
        praksistyper.find(
          (praksistype) => praksistype.kode === initPraksistype.kode,
        ),
      )
    );
  };
};

const PracticeTypes = () => {
  const {
    praksistyper: typer,
    dispatchAgreementEdit,
    canEditAgreement,
  } = useAgreementContext();
  const praksistyper = useMemo(() => typer || [], [typer]);

  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      modalTitle: formatMessage({ id: translations.modalTitle.id }),
      modalSubtitle: formatMessage({ id: translations.modalSubtitle.id }),
      addPracticeType: formatMessage({ id: translations.addPracticeType.id }),
      removePracticeTypes: formatMessage({
        id: translations.removePracticeTypes.id,
      }),
    }),
    [formatMessage],
  );

  const praksistyperHasChanged = usePraksistyperHasChanged();

  const setPraksistyper = (practiceTypes?: PraksistypeDto[]) =>
    dispatchAgreementEdit({
      type: AgreementStateActionType.SET_PRACTICE_TYPES,
      payload: {
        content: practiceTypes,
        praksistypeHasChanged: praksistyperHasChanged(practiceTypes || []),
      },
    });

  const onClose = (newPracticeType: PraksistypeDto | undefined) => {
    if (newPracticeType) {
      setPraksistyper(praksistyper.concat(newPracticeType));
    }

    setModalIsOpen(false);
  };

  const hasPracticeTypes = praksistyper.length > 0;

  return (
    <div id={'praksistyper-section'}>
      {hasPracticeTypes && (
        <PracticeTypesTable
          removePraksistype={(kode) =>
            setPraksistyper(
              praksistyper.filter((practiceType) => practiceType.kode !== kode),
            )
          }
        />
      )}
      {canEditAgreement && (
        <>
          <div
            className={classNames(
              commonCss.actionBtns,
              hasPracticeTypes && commonCss.btnMargin,
            )}
          >
            {hasPracticeTypes && (
              <Button.Tertiary
                className={commonCss.button}
                onClick={() => setPraksistyper()}
              >
                {labels.removePracticeTypes}
              </Button.Tertiary>
            )}
            <Button.Secondary
              data-cy={'add-praksistype-btn'}
              className={commonCss.button}
              onClick={() => setModalIsOpen(true)}
            >
              {labels.addPracticeType}
            </Button.Secondary>
          </div>
          <PracticeTypesModal onClose={onClose} isOpen={modalIsOpen} />
        </>
      )}
    </div>
  );
};

enum PracticeTypesTableHeader {
  TYPEKODE = 'TYPEKODE',
  TYPENAVN = 'TYPENAVN',
  HANDLING = 'HANDLING',
}

enum PracticeTypesTableHeaderView {
  TYPEKODE = 'TYPEKODE',
  TYPENAVN = 'TYPENAVN',
}

interface PracticeTypesTableProps {
  removePraksistype: (kode: string) => void;
}

const PracticeTypesTable: React.FC<
  React.PropsWithChildren<PracticeTypesTableProps>
> = ({ removePraksistype }) => {
  const { praksistyper: typer, canEditAgreement } = useAgreementContext();
  const praksistyper = useMemo(() => typer || [], [typer]);

  const { formatMessage, locale } = useIntl();

  const headerKeys = useMemo(
    () =>
      Object.keys(
        canEditAgreement
          ? PracticeTypesTableHeader
          : PracticeTypesTableHeaderView,
      ),
    [canEditAgreement],
  );
  const initActiveCol = PracticeTypesTableHeader.TYPEKODE;

  const compare = Intl.Collator(locale || 'nb').compare;
  const sort = useCallback(
    (col: number, a: PraksistypeDto, b: PraksistypeDto) => {
      switch (col) {
        case headerKeys.indexOf(PracticeTypesTableHeader.TYPEKODE):
          return compare(a.kode, b.kode);
        case headerKeys.indexOf(PracticeTypesTableHeader.TYPENAVN):
          return compare(a.navn, b.navn);
      }
      return 0;
    },
    [compare, headerKeys],
  );

  const [activeCol, setActiveCol] = useState<number>(
    headerKeys.indexOf(initActiveCol),
  );
  const [ascending, setAscending] = useState<boolean>(false);
  const [sortedPracticeTypes, setSortedPracticeTypes] =
    useState<PraksistypeDto[]>(praksistyper);

  // praksistyper is mutable and sort is therefore put in useEffect-hook
  useEffect(() => {
    const newSortedPracticeTypes = [...praksistyper].sort((a, b) =>
      ascending ? sort(activeCol, b, a) : sort(activeCol, a, b),
    );

    const isEqual =
      newSortedPracticeTypes.length === sortedPracticeTypes.length &&
      sortedPracticeTypes.every((previouslySortedPracticeType, index) =>
        praksistyperOrEmnerAreEqual(
          previouslySortedPracticeType,
          newSortedPracticeTypes[index],
        ),
      );

    if (!isEqual) {
      setSortedPracticeTypes(newSortedPracticeTypes);
    }
  }, [praksistyper, activeCol, ascending, sort, sortedPracticeTypes]);

  const labels = useMemo(
    () => ({
      practiceTypeCode: formatMessage({
        id: translations.practiceTypeCode.id,
      }),
      practiceTypeName: formatMessage({
        id: translations.practiceTypeName.id,
      }),
      action: formatMessage({
        id: translations.action.id,
      }),
      delete: formatMessage({
        id: translations.delete.id,
      }),
    }),
    [formatMessage],
  );

  const tableHeaders = useMemo(
    () => ({
      [PracticeTypesTableHeader.TYPEKODE]: labels.practiceTypeCode,
      [PracticeTypesTableHeader.TYPENAVN]: labels.practiceTypeName,
      [PracticeTypesTableHeader.HANDLING]: labels.action,
    }),
    [labels],
  );

  const tableHeadersView = useMemo(
    () => ({
      [PracticeTypesTableHeader.TYPEKODE]: labels.practiceTypeCode,
      [PracticeTypesTableHeader.TYPENAVN]: labels.practiceTypeName,
    }),
    [labels],
  );

  const headers = useMemo(
    () =>
      Object.entries(canEditAgreement ? tableHeaders : tableHeadersView).map(
        ([type, title]) => {
          const index = headerKeys.indexOf(type);
          return (
            <TableCellTh
              key={`${title}-${type}`}
              colIndex={index}
              activeCol={index === activeCol}
              setActiveCol={setActiveCol}
              canSort={type !== PracticeTypesTableHeader.HANDLING}
              sort={(col, asc) => {
                setActiveCol(col);
                setAscending(asc);
              }}
              align={
                type === PracticeTypesTableHeader.HANDLING ? 'right' : undefined
              }
            >
              {title}
            </TableCellTh>
          );
        },
      ),
    [tableHeaders, headerKeys, activeCol, canEditAgreement, tableHeadersView],
  );

  const rows = useMemo(
    () => (
      <>
        {sortedPracticeTypes.map((practiceType) => (
          <TableRow key={practiceType.kode}>
            <TableCellTd
              header={tableHeaders[PracticeTypesTableHeader.TYPEKODE]}
            >
              {practiceType.kode}
            </TableCellTd>
            <TableCellTd
              header={tableHeaders[PracticeTypesTableHeader.TYPENAVN]}
            >
              {practiceType.navn}
            </TableCellTd>
            {canEditAgreement && (
              <TableCellTd
                header={tableHeaders[PracticeTypesTableHeader.HANDLING]}
                align={'right'}
              >
                <Button.Tertiary
                  onClick={() => removePraksistype(practiceType.kode)}
                  id={`remove-praksistype-btn-${practiceType.kode}`}
                >
                  {labels.delete}
                </Button.Tertiary>
              </TableCellTd>
            )}
          </TableRow>
        ))}
      </>
    ),
    [
      tableHeaders,
      sortedPracticeTypes,
      labels.delete,
      removePraksistype,
      canEditAgreement,
    ],
  );

  return (
    <Table>
      <TableHeader>
        <TableRow top>{headers}</TableRow>
      </TableHeader>
      <TableMain>{rows}</TableMain>
    </Table>
  );
};

interface ModalProps {
  onClose: (practiceType?: PraksistypeDto) => void;
  isOpen: boolean;
}

const PracticeTypesModal: React.FC<React.PropsWithChildren<ModalProps>> = ({
  onClose,
  isOpen,
}) => {
  const { praksistyper: typer } = useAgreementContext();
  const alreadyChosen = useMemo(() => typer || [], [typer]);

  const { praksistypeApi } = useContext(StudiestedApiContext);
  const { data: praksistyperEmner } = useLoader(
    () => praksistypeApi.praksistyperMedEmnerGet(),
    [],
  );
  const { formatMessage } = useIntl();

  const [practiceType, setPracticeType] = useState<PraksistypeMedEmnerDto>();

  const labels = useMemo(
    () => ({
      addPracticeType: formatMessage({ id: translations.addPracticeType.id }),
      cancel: formatMessage({ id: translations.cancel.id }),
      modalTitle: formatMessage({ id: translations.modalTitle.id }),
      modalSubtitle: formatMessage({ id: translations.modalSubtitle.id }),
      practiceTypesLabel: formatMessage({
        id: translations.practiceTypesLabel.id,
      }),
    }),
    [formatMessage],
  );

  const close = (save: boolean) => {
    save ? onClose(practiceType?.praksistype) : onClose();
    setPracticeType(undefined);
  };

  const {
    documentHeight,
    elementHeight: modalHeight,
    elementRef: modalContentRef,
  } = useDynamicDocumentAndElementHeights();

  return (
    <ConfirmationDialog
      extraClasses={[commonCss.modal]}
      contentCss={getCssBasedOnModalHeight({ documentHeight, modalHeight })}
      isOpen={isOpen}
      title={labels.modalTitle}
      subtitle={labels.modalSubtitle}
      submitLabel={labels.addPracticeType}
      cancelLabel={labels.cancel}
      onClose={close}
      submitDisabled={!practiceType}
    >
      <div ref={modalContentRef}>
        {praksistyperEmner ? (
          <>
            <Autocomplete
              id={'praksistype-select'}
              label={labels.practiceTypesLabel}
              onChange={(_, value) =>
                setPracticeType(
                  praksistyperEmner.find(
                    (type) => type.praksistype.kode === value?.id,
                  ),
                )
              }
              options={praksistyperEmner
                .filter(
                  (praksistypeMedEmne) =>
                    !alreadyChosen.some((alreadyChosenPraksistype) =>
                      praksistyperOrEmnerAreEqual(
                        alreadyChosenPraksistype,
                        praksistypeMedEmne.praksistype,
                      ),
                    ),
                )
                .map((praksistypeMedEmne) => ({
                  id: praksistypeMedEmne.praksistype.kode,
                  navn: praksistypeMedEmne.praksistype.navn,
                  customSearchString: `${praksistypeMedEmne.praksistype.kode}${praksistypeMedEmne.praksistype.navn}`,
                }))}
              renderOption={(option) => (
                <PadPraksistypeWithKode
                  praksistype={{ kode: option.id, navn: option.navn }}
                />
              )}
              required
            />
            {practiceType && <EmneTable emner={practiceType.emner} />}
          </>
        ) : (
          <Spinner />
        )}
      </div>
    </ConfirmationDialog>
  );
};

enum EmneTableHeader {
  EMNEKODE = 'EMNEKODE',
  EMNENAVN = 'EMNENAVN',
}

interface EmneTableProps {
  emner: EmneDto[];
}

const EmneTable: React.FC<React.PropsWithChildren<EmneTableProps>> = ({
  emner,
}) => {
  const { formatMessage, locale } = useIntl();

  const headerKeys = useMemo(() => Object.keys(EmneTableHeader), []);
  const initActiveCol = EmneTableHeader.EMNEKODE;

  const compare = Intl.Collator(locale || 'nb').compare;
  const sort = useCallback(
    (col: number, a: EmneDto, b: EmneDto) => {
      switch (col) {
        case headerKeys.indexOf(EmneTableHeader.EMNEKODE):
          return compare(a.kode, b.kode);
        case headerKeys.indexOf(EmneTableHeader.EMNENAVN):
          return compare(a.navn, b.navn);
      }
      return 0;
    },
    [compare, headerKeys],
  );

  const [activeCol, setActiveCol] = useState<number>(
    headerKeys.indexOf(initActiveCol),
  );
  const [ascending, setAscending] = useState<boolean>(false);
  const [sortedEmner, setSortedEmner] = useState<EmneDto[]>(emner);

  // emner is mutable and sort is therefore put in useEffect-hook
  useEffect(() => {
    const newSortedEmner = [...emner].sort((a, b) =>
      ascending ? sort(activeCol, b, a) : sort(activeCol, a, b),
    );

    const isEqual =
      newSortedEmner.length === sortedEmner.length &&
      sortedEmner.every((previouslySortedEmne, index) =>
        praksistyperOrEmnerAreEqual(
          previouslySortedEmne,
          newSortedEmner[index],
        ),
      );

    if (!isEqual) {
      setSortedEmner(newSortedEmner);
    }
  }, [emner, activeCol, ascending, sort, sortedEmner]);

  const labels = useMemo(
    () => ({
      emneCode: formatMessage({
        id: translations.emneCode.id,
      }),
      emneName: formatMessage({
        id: translations.emneName.id,
      }),
    }),
    [formatMessage],
  );

  const tableHeaders = useMemo(
    () => ({
      [EmneTableHeader.EMNEKODE]: labels.emneCode,
      [EmneTableHeader.EMNENAVN]: labels.emneName,
    }),
    [labels],
  );

  const headers = useMemo(
    () =>
      Object.entries(tableHeaders).map(([type, title]) => {
        const index = headerKeys.indexOf(type);
        return (
          <TableCellTh
            key={`${title}-${type}`}
            colIndex={index}
            activeCol={index === activeCol}
            setActiveCol={setActiveCol}
            sort={(col, asc) => {
              setActiveCol(col);
              setAscending(asc);
            }}
            canSort
          >
            {title}
          </TableCellTh>
        );
      }),
    [tableHeaders, activeCol, headerKeys],
  );

  const rows = useMemo(
    () => (
      <>
        {sortedEmner.map((emne) => (
          <TableRow key={emne.kode}>
            <TableCellTd header={tableHeaders[EmneTableHeader.EMNEKODE]}>
              {emne.kode}
            </TableCellTd>
            <TableCellTd header={tableHeaders[EmneTableHeader.EMNENAVN]}>
              {emne.navn}
            </TableCellTd>
          </TableRow>
        ))}
      </>
    ),
    [tableHeaders, sortedEmner],
  );

  return (
    <Table>
      <TableHeader>
        <TableRow top>{headers}</TableRow>
      </TableHeader>
      <TableMain>{rows}</TableMain>
    </Table>
  );
};

export const PadPraksistypeWithKode: React.FC<
  React.PropsWithChildren<{
    praksistype: PraksistypeDto;
  }>
> = ({ praksistype }) => (
  <>
    <span className={css.praksistypekodePadding}>{praksistype.kode}</span>
    <span className={css.praksistypekodeNavn}>{praksistype.navn}</span>
  </>
);

export default PracticeTypes;
